import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import ProtectedImage from "@/components/ProtectedImage";

const CATEGORIES = [
  { id: "solo", label: "Solo" },
  { id: "couple", label: "Couples" },
  { id: "family", label: "Family" },
  { id: "pet", label: "Pets" },
  { id: "wedding", label: "Weddings" },
  { id: "friends", label: "Friends" },
  { id: "gifts", label: "Gifts" },
  { id: "anniversary", label: "Anniversary" },
  { id: "special_occasion", label: "Special Occasion" },
  { id: "party", label: "Party" },
  { id: "all", label: "All" },
];
const ROTS = ["-3deg", "2deg", "-2deg", "3deg", "-1deg", "2deg"];

export default function CategorizedGallery({ images = [] }) {
  const [active, setActive] = useState("all");

  const available = useMemo(() => {
    const present = new Set(images.map((i) => i.category));
    return CATEGORIES.filter((c) => c.id === "all" || present.has(c.id));
  }, [images]);

  const filtered = useMemo(
    () => (active === "all" ? images : images.filter((i) => i.category === active)),
    [images, active]
  );

  return (
    <div data-testid="categorized-gallery">
      <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
        {available.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} data-testid={`gallery-filter-${c.id}`}
            className={`brut-btn !py-2 !px-5 text-sm ${active === c.id ? "bg-bubble text-white" : "bg-white"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="brut-card p-6 text-ink/60">No pieces in this category yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((c, i) => (
            <motion.div key={c.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i % 4) * 0.06 }}
              className="bg-white p-3 border-[3px] border-ink rounded-2xl shadow-brutlg hover:-translate-y-1 transition-transform"
              style={{ transform: `rotate(${ROTS[i % ROTS.length]})` }} data-testid={`gallery-item-${i}`}>
              <ProtectedImage src={c.src} alt={c.caption || "caricature"}
                className="w-full h-72 object-contain bg-cream-solid rounded-lg border-2 border-ink" />
              <div className="flex items-center justify-between mt-2 px-1">
                {c.caption ? <p className="font-display font-semibold">{c.caption}</p> : <span />}
                <span className="tag-pill bg-sun !py-0.5 text-xs capitalize">{c.category}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
